import { useState } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  useWorkOrder,
  useUpdateWorkOrder,
  useDeleteWorkOrder,
  useChangeWorkOrderStatus,
} from '@/features/service/hooks'
import { useBoat } from '@/features/inventory/hooks'
import { WorkOrderForm } from '@/features/service/work-order-form'
import { useDirtyClose } from '@/hooks/use-dirty-close'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  ArrowLeft,
  User,
  Ship,
  ExternalLink,
  CalendarDays,
  Clock,
  Pencil,
  Trash2,
  Play,
  Pause,
  CheckCircle2,
  Loader2,
  MessageSquare,
  Gauge,
  Ban,
  RotateCcw,
  Printer,
} from 'lucide-react'
import type {
  WorkOrderStatus,
  WorkOrderTransitionStatus,
  WorkOrderWriteBody,
} from '@/types/service'
import { WORK_ORDER_STATUS_LABELS } from '@/types/service'
import { MessagesSidebar } from '@/features/messaging/components/messages-sidebar'
import { useCustomer } from '@/features/customers/hooks'
import { customerDisplayName } from '@/features/customers/customer-utils'
import { useInspectionsForWorkOrder } from '@/features/inspection/hooks'
import { PartsCard } from '@/features/parts/work-order-parts-card'
import { requireFlow } from '@/features/auth/guards'

export const Route = createFileRoute('/_authenticated/service/$workOrderId')({
  beforeLoad: requireFlow('service'),
  component: WorkOrderDetailPage,
})

const statusVariantMap: Record<
  WorkOrderStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending: 'outline',
  in_progress: 'secondary',
  on_hold: 'outline',
  completed: 'default',
  canceled: 'destructive',
}

// Valid next statuses from the domain state machine. Cancel confirms first;
// completed/canceled reopen directly (the backend clears the completion
// timestamp on reopen).
const validTransitions: Record<
  WorkOrderStatus,
  { label: string; target: WorkOrderTransitionStatus; icon: typeof Play; confirm?: boolean }[]
> = {
  pending: [
    { label: 'Start', target: 'in_progress', icon: Play },
    { label: 'Pause', target: 'on_hold', icon: Pause },
    { label: 'Complete', target: 'completed', icon: CheckCircle2 },
    { label: 'Cancel', target: 'canceled', icon: Ban, confirm: true },
  ],
  in_progress: [
    { label: 'Pause', target: 'on_hold', icon: Pause },
    { label: 'Complete', target: 'completed', icon: CheckCircle2 },
    { label: 'Cancel', target: 'canceled', icon: Ban, confirm: true },
  ],
  on_hold: [
    { label: 'Resume', target: 'in_progress', icon: Play },
    { label: 'Complete', target: 'completed', icon: CheckCircle2 },
    { label: 'Cancel', target: 'canceled', icon: Ban, confirm: true },
  ],
  completed: [{ label: 'Reopen', target: 'in_progress', icon: RotateCcw }],
  canceled: [{ label: 'Reopen', target: 'in_progress', icon: RotateCcw }],
}

function WorkOrderDetailPage() {
  const { workOrderId } = Route.useParams()
  const navigate = useNavigate()
  const { data: workOrder, isLoading, isError } = useWorkOrder(workOrderId)
  const { data: boat } = useBoat(workOrder?.boat_id ?? '')
  const { data: customer } = useCustomer(boat?.customer_id ?? '')

  const [editOpen, setEditOpen] = useState(false)
  const [formDirty, setFormDirty] = useState(false)
  const { guardOpenChange: guardEditOpen, discardGuard: editDirtyGuard } = useDirtyClose(
    editOpen,
    setEditOpen,
    formDirty,
  )
  const [editError, setEditError] = useState<string | null>(null)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const [messageOpen, setMessageOpen] = useState(false)
  const [completeOpen, setCompleteOpen] = useState(false)
  const [hourReading, setHourReading] = useState<string | null>(null)

  const updateMutation = useUpdateWorkOrder()
  const deleteMutation = useDeleteWorkOrder()
  const statusMutation = useChangeWorkOrderStatus()

  const handleUpdate = (values: WorkOrderWriteBody) => {
    const { boat_id: _, ...rest } = values
    setEditError(null)
    updateMutation.mutate(
      { id: workOrderId, body: rest },
      {
        onSuccess: () => setEditOpen(false),
        onError: (err) =>
          setEditError(err instanceof Error ? err.message : 'Failed to update work order'),
      },
    )
  }

  const handleDelete = () => {
    deleteMutation.mutate(workOrderId, {
      onSuccess: () => {
        setDeleteOpen(false)
        navigate({ to: '/service' })
      },
    })
  }

  const handleStatusChange = (target: WorkOrderTransitionStatus, confirm?: boolean) => {
    if (target === 'completed') {
      // Prefill with the boat's current reading once, then let the technician edit.
      if (hourReading === null && boat?.engine_hours != null) {
        setHourReading(String(boat.engine_hours))
      }
      setCompleteOpen(true)
      return
    }
    if (confirm) {
      setCancelOpen(true)
      return
    }
    statusMutation.mutate({ id: workOrderId, status: target })
  }

  const handleCancel = () => {
    statusMutation.mutate(
      { id: workOrderId, status: 'canceled' },
      { onSuccess: () => setCancelOpen(false) },
    )
  }

  const handleComplete = () => {
    const hours =
      hourReading === null || hourReading.trim() === '' ? undefined : Number(hourReading)
    statusMutation.mutate(
      {
        id: workOrderId,
        status: 'completed',
        engineHoursAtService: Number.isFinite(hours) ? hours : undefined,
      },
      { onSuccess: () => setCompleteOpen(false) },
    )
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-64" />
        <div className="grid gap-4 md:grid-cols-2">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    )
  }

  if (isError || !workOrder) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-destructive">Work order not found.</p>
        <Link to="/service">
          <Button variant="outline">Back to Service</Button>
        </Link>
      </div>
    )
  }

  const transitions = validTransitions[workOrder.status]

  return (
    <div className="flex flex-col gap-6">
      <Link to="/service">
        <Button variant="ghost" size="sm" className="gap-2 print:hidden">
          <ArrowLeft className="h-4 w-4" /> Back to Service
        </Button>
      </Link>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between print:hidden">
        <div className="min-w-0">
          <h1 className="break-words text-3xl font-bold tracking-tight">{workOrder.title}</h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Badge variant={statusVariantMap[workOrder.status]} className="text-md">
            {WORK_ORDER_STATUS_LABELS[workOrder.status]}
          </Badge>
          <Badge variant="outline" className="text-md capitalize">
            {workOrder.priority}
          </Badge>
          {boat?.customer_id && (
            <>
              <Button variant="outline" size="sm" onClick={() => setMessageOpen(true)}>
                <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Messages
              </Button>
              <MessagesSidebar
                open={messageOpen}
                onOpenChange={setMessageOpen}
                customerId={boat.customer_id}
                customerName={customer ? customerDisplayName(customer) : undefined}
                workOrderId={workOrderId}
              />
            </>
          )}
          <>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)}>
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="mr-1.5 h-3.5 w-3.5" /> Print
            </Button>
          </>
        </div>
      </div>

      {/* Status Action Buttons */}
      {transitions.length > 0 && (
        <div className="flex flex-wrap gap-2 print:hidden">
          {transitions.map(({ label, target, icon: Icon, confirm }) => (
            <Button
              key={target}
              variant={target === 'completed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleStatusChange(target, confirm)}
              disabled={statusMutation.isPending}
            >
              {statusMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Icon className="mr-1.5 h-3.5 w-3.5" />
              )}
              {label}
            </Button>
          ))}
        </div>
      )}

      {/* Content Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Assignment Details</CardTitle>
            <CardDescription>Personnel and scheduling information.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <User className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Assigned Technician</p>
                <span className="font-medium">{workOrder.assigned_technician || 'Unassigned'}</span>
              </div>
            </div>
            {customer && (
              <div className="flex items-center gap-4">
                <User className="h-5 w-5 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="text-sm text-muted-foreground">Customer</p>
                  <Link
                    to="/sales/$customerId"
                    params={{ customerId: customer.id }}
                    className="font-medium underline-offset-4 hover:underline"
                  >
                    {customerDisplayName(customer)}
                  </Link>
                </div>
              </div>
            )}
            <div className="flex items-center gap-4">
              <CalendarDays className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <span className="font-medium">
                  {new Date(workOrder.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            {workOrder.scheduled_date && (
              <div className="flex items-center gap-4">
                <CalendarDays className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Scheduled</p>
                  <span className="font-medium">
                    {new Date(workOrder.scheduled_date).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}
            {workOrder.due_date && (
              <div className="flex items-center gap-4">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Due</p>
                  <span className="font-medium">
                    {new Date(workOrder.due_date).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}
            {workOrder.completed_at && (
              <div className="flex items-center gap-4">
                <CheckCircle2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Completed</p>
                  <span className="font-medium">
                    {new Date(workOrder.completed_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Associated Boat</CardTitle>
            <CardDescription>The boat receiving this service.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3 p-4 border rounded-lg">
              <div className="flex min-w-0 items-center gap-3">
                <Ship className="h-6 w-6 shrink-0 text-primary" />
                <span className="truncate font-medium text-lg">{workOrder.boat_name}</span>
              </div>
              <Link to="/inventory/$boatId" params={{ boatId: workOrder.boat_id }}>
                <Button variant="outline" size="sm" className="gap-2">
                  View Boat <ExternalLink className="h-3 w-3" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Issue Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {workOrder.description || 'No description provided.'}
            </p>
          </CardContent>
        </Card>

        <IntakeInspectionsCard workOrderId={workOrderId} />

        <PartsCard workOrderId={workOrderId} />
      </div>

      {/* Edit Dialog */}
      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          guardEditOpen(open)
          if (!open) setEditError(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Work Order</DialogTitle>
          </DialogHeader>
          <WorkOrderForm
            onDirtyChange={setFormDirty}
            defaultValues={{
              title: workOrder.title,
              description: workOrder.description,
              priority: workOrder.priority,
              assigned_technician: workOrder.assigned_technician,
              scheduled_date: workOrder.scheduled_date
                ? workOrder.scheduled_date.split('T')[0]
                : '',
              due_date: workOrder.due_date ? workOrder.due_date.split('T')[0] : '',
            }}
            onSubmit={handleUpdate}
            isPending={updateMutation.isPending}
            submitLabel="Save Changes"
            preselectedBoatId={workOrder.boat_id}
          />
          {editError && <p className="text-sm text-destructive">{editError}</p>}
          {editDirtyGuard}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Work Order</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this work order? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog: closing a work order is destructive-ish
          (it stops showing as open work and releases the boat). */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="h-5 w-5" /> Cancel work order
            </DialogTitle>
            <DialogDescription>
              This closes the work order as canceled. The boat releases from service if no other
              open work remains, and the order can be reopened later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Keep Open
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={statusMutation.isPending}
            >
              {statusMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cancel Work Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Complete-with-hour-reading Dialog: the reading updates the boat's
          engine hours and rolls any linked hour-based schedule forward. */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gauge className="h-5 w-5" /> Complete work order
            </DialogTitle>
            <DialogDescription>
              Record the boat's hour-meter reading at service. It updates the boat's engine hours
              and advances hour-based schedules; leave it blank to complete without a reading.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-1">
            <Label htmlFor="hour-reading">Engine hours at service</Label>
            <Input
              id="hour-reading"
              type="number"
              min={0}
              step="0.1"
              placeholder={boat?.engine_hours != null ? String(boat.engine_hours) : 'e.g. 450'}
              value={hourReading ?? ''}
              onChange={(e) => setHourReading(e.target.value)}
            />
            {boat?.engine_hours != null && (
              <p className="text-xs text-muted-foreground">
                Current reading on file: {boat.engine_hours.toLocaleString()} hrs
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleComplete} disabled={statusMutation.isPending}>
              {statusMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <CheckCircle2 className="mr-1.5 h-4 w-4" /> Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Intake inspections that fed this work order: a completed inspection
// attaches to the boat's oldest open work order, so this card shows the
// field observations behind the job. Empty state stays visible — it explains
// why nothing is here.
function IntakeInspectionsCard({ workOrderId }: { workOrderId: string }) {
  const { data: inspections, isLoading } = useInspectionsForWorkOrder(workOrderId)

  return (
    <Card className="md:col-span-2">
      <CardHeader>
        <CardTitle>Intake inspections</CardTitle>
        <CardDescription>
          Boat intake findings recorded against this job (the inspection attaches to the oldest open
          work order).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : !inspections || inspections.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No intake inspections attached. Record one from the Inspection flow.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {inspections.map((insp) => (
              <Link
                key={insp.id}
                to="/inspection/$inspectionId"
                params={{ inspectionId: insp.id }}
                className="flex items-center justify-between gap-3 rounded-lg border p-3 text-sm transition-colors hover:bg-accent"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Ship className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="truncate font-medium">
                      {insp.year} {insp.make} {insp.model}
                    </div>
                    <div className="text-muted-foreground truncate text-xs">
                      HIN {insp.hin || '—'}
                      {insp.notes ? ` · ${insp.notes}` : ''}
                    </div>
                  </div>
                </div>
                <div className="text-muted-foreground flex shrink-0 items-center gap-2 text-xs">
                  {new Date(insp.performed_at).toLocaleDateString()}
                  <ExternalLink className="h-3 w-3" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
